import React from 'react';

import { FormattedDate, FormattedMessage, useIntl } from 'react-intl';

import Lozenge from '@atlaskit/lozenge/lozenge';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box } from '@atlaskit/primitives/box';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Inline } from '@atlaskit/primitives/inline';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { xcss } from '@atlaskit/primitives/xcss/xcss';
import type { SearchResultWorkItemPartial } from '@atlassian/dynamic-cards-search-client/graphql/third-party-graph-entity';

import { Avatar } from '../../../../../../../common/ui/avatar';
import { LastModified } from '../../../../../../../common/ui/last-modified';
import { RestrictedIcon } from '../../../../../../../common/ui/restricted-icon';
import { SearchMetadata } from '../../../../../../../common/ui/search-metadata';
import { SearchResult } from '../../../../../../../common/ui/search-result';
import {
	enrichExcerpt,
	isNonPublic,
	processExcerpt,
} from '../../../../../../../common/utils/search-result-utils';
import {
	useBootstrap,
	usePrimaryProduct,
	useSelectedProduct,
} from '../../../../../../../controllers/store';
import { messages } from './messages';

const nameContainer = xcss({
	whiteSpace: 'nowrap',
});

const statusAndDateContainerStyles = xcss({
	display: 'flex',
	alignItems: 'center',
	gap: 'space.100',
});

interface WorkItemSearchResultProps {
	icon: React.ReactNode;
	partial: SearchResultWorkItemPartial;
	position?: number;
	product?: string;
}

const STATUS_REGEX = /[\s-]+/g;
const getStatusLozenge = (name: string) => {
	name = name.replace(STATUS_REGEX, '').toLowerCase();
	switch (true) {
		case ['onhold'].includes(name):
			return 'neutral';

		case ['solved', 'complete', 'completed', 'open'].includes(name):
			return 'success';

		case ['offtrack', 'untriaged'].includes(name):
			return 'danger';

		case ['atrisk'].includes(name):
			return 'warning';

		case ['todo', 'new', 'pending', 'created'].includes(name):
			return 'discovery';

		case ['inprogress', 'ontrack'].includes(name):
			return 'information';
	}
	return 'information'; // for custom status labels
};

export const WorkItemSearchResult = ({
	partial,
	position,
	icon,
	product,
}: WorkItemSearchResultProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const [selectedProduct] = useSelectedProduct();
	const [primaryProduct] = usePrimaryProduct();
	const [{ cloudId }] = useBootstrap();

	const { lastModifiedDate, url, permissionLevel, entity, excerpt, title, subtype } = partial;
	const { assignee, createdBy, dueDate, id, projectName, status } = entity || {};

	const lastModifiedComponent = lastModifiedDate ? (
		<LastModified lastModified={lastModifiedDate} />
	) : null;

	const padlockIcon = isNonPublic(permissionLevel) ? (
		<RestrictedIcon permissionLevel={permissionLevel} />
	) : null;

	const getStatusAndDate = () => {
		if (!status) {
			return null;
		}

		return (
			<Box xcss={statusAndDateContainerStyles}>
				<Lozenge appearance={getStatusLozenge(status)}>{status}</Lozenge>
				{dueDate && (
					<>
						<FormattedMessage {...messages.for} />
						<Lozenge>
							<FormattedDate value={dueDate} month="short" day="numeric" />
						</Lozenge>
					</>
				)}
			</Box>
		);
	};

	const getAssigneeOrCreator = () => {
		let fullName: string | '', firstName: string, verbMessage: string;
		if (assignee?.thirdPartyUser?.name) {
			fullName = assignee?.thirdPartyUser?.name || '';
			verbMessage = formatMessage(messages.isAssigned);
		} else {
			fullName = createdBy?.thirdPartyUser?.name || '';
			verbMessage = formatMessage(messages.created);
		}
		firstName = fullName?.split(' ')?.[0] || formatMessage(messages.externalPersonLabel);

		return (
			<Inline space="space.025" alignBlock="center">
				<Avatar name={fullName} />
				<Box xcss={nameContainer}>
					{firstName} {verbMessage}
				</Box>
			</Inline>
		);
	};

	let label = formatMessage(messages.workItem);
	if (subtype === 'task') {
		label = formatMessage(messages.taskLabel);
	} else if (subtype === 'project') {
		label = formatMessage(messages.projectLabel);
	} else if (subtype === 'ticket') {
		label = formatMessage(messages.ticketLabel);
	}

	return (
		<SearchResult
			id={partial.id}
			icon={icon}
			description={processExcerpt(excerpt, true)}
			headerMetadata={
				<SearchMetadata
					product={product}
					productSelected={!!selectedProduct}
					parts={[
						label,
						['identifier', subtype === 'ticket' && id ? `#${id}` : projectName],
						['last-modified', lastModifiedComponent],
						['padlock', padlockIcon],
						['status', getStatusAndDate()],
						['person', getAssigneeOrCreator()],
					]}
				/>
			}
			title={enrichExcerpt(title)}
			url={url}
			isThirdParty
			cloudId={cloudId}
			primaryProduct={primaryProduct}
			position={position}
			partial={partial}
		/>
	);
};
